import React, { useState } from "react";
import { ITreatment, ISubTreatment } from "../types/clinic.types";
import { ConfirmationModal } from "./ConfirmationModal";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

interface Props {
  treatment: ITreatment;
  onDelete: () => void;
  onEdit: () => void;
  onAddSub: () => void;
  onEditSub: (sub: ISubTreatment) => void;
  onDeleteSub: (subId: string) => void;
}

function formatPrice(value: string | number | undefined, currency: string) {
  if (value === "" || value === undefined || value === null) return "—";
  return `${value} ${currency}`;
}

function displayNote(note: string | number | undefined) {
  if (note === "" || note === undefined || note === null || note === 0) return "—";
  return String(note);
}

const thClass =
  "px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wide text-slate-500 whitespace-nowrap bg-slate-50/80";
const tdClass = "px-3 py-2.5 text-xs text-slate-700 align-top border-t border-slate-100";

export const TreatmentCard: React.FC<Props> = ({
  treatment,
  onDelete,
  onEdit,
  onAddSub,
  onEditSub,
  onDeleteSub,
}) => {
  const [subDeleteCandidate, setSubDeleteCandidate] = useState<string | null>(
    null
  );

  const handleDeleteConfirm = () => {
    if (subDeleteCandidate) {
      onDeleteSub(subDeleteCandidate);
      setSubDeleteCandidate(null);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* Main treatment row */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr>
              <th className={thClass}>Treatment name</th>
              <th className={thClass}>Description</th>
              <th className={thClass}>Local price (LKR)</th>
              <th className={thClass}>Foreigner price (USD)</th>
              <th className={thClass}>Sub-treatments</th>
              <th className={`${thClass} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-white hover:bg-slate-50/50">
              <td className={`${tdClass} font-semibold text-slate-900`}>
                {treatment.name || "—"}
              </td>
              <td className={tdClass}>{treatment.description || "—"}</td>
              <td className={tdClass}>
                {formatPrice(treatment.localPrice, "LKR")}
              </td>
              <td className={tdClass}>
                {formatPrice(treatment.foreginPrice, "USD")}
              </td>
              <td className={tdClass}>
                <span className="inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                  {treatment.subTreatments.length}
                </span>
              </td>
              <td className={`${tdClass} text-right`}>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    className="p-1.5 text-slate-600 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                    onClick={onEdit}
                    aria-label="Edit treatment"
                    title="Edit"
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </button>
                  <button
                    type="button"
                    className="p-1.5 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                    onClick={onDelete}
                    aria-label="Delete treatment"
                    title="Delete"
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Sub-treatments table */}
      <div className="border-t border-slate-200 bg-slate-50/40">
        <div className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4">
          <h5 className="text-[11px] font-bold uppercase tracking-wide text-slate-600 mb-0">
            Sub-treatments
          </h5>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 self-start rounded-lg bg-gradient-to-r from-primary to-primaryLight px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm transition-all hover:shadow-md hover:shadow-primary/20"
            onClick={onAddSub}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            Add sub-treatment
          </button>
        </div>

        {treatment.subTreatments.length === 0 ? (
          <p className="px-4 pb-3 text-xs text-slate-500">No sub-treatments yet.</p>
        ) : (
          <div className="overflow-x-auto pb-1">
            <table className="w-full min-w-[1100px] border-collapse">
              <thead>
                <tr>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Description</th>
                  <th className={thClass}>Duration (min)</th>
                  <th className={thClass}>Local price</th>
                  <th className={thClass}>Min local</th>
                  <th className={thClass}>Max local</th>
                  <th className={thClass}>Foreigner price</th>
                  <th className={thClass}>Min foreigner</th>
                  <th className={thClass}>Max foreigner</th>
                  <th className={thClass}>Note</th>
                  <th className={`${thClass} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {treatment.subTreatments.map((sub) => (
                  <tr
                    key={sub.id}
                    className="bg-white hover:bg-slate-50/80 transition-colors"
                  >
                    <td className={`${tdClass} font-medium text-slate-900`}>
                      {sub.subTreatment || "—"}
                    </td>
                    <td className={`${tdClass} max-w-[180px]`}>
                      <span className="line-clamp-3">{sub.description || "—"}</span>
                    </td>
                    <td className={tdClass}>{sub.duration ?? "—"}</td>
                    <td className={tdClass}>
                      {formatPrice(sub.localPrice, "LKR")}
                    </td>
                    <td className={tdClass}>
                      {formatPrice(sub.minlocalPrice, "LKR")}
                    </td>
                    <td className={tdClass}>
                      {formatPrice(sub.maxlocalPrice, "LKR")}
                    </td>
                    <td className={tdClass}>
                      {formatPrice(sub.foreignerPrice, "USD")}
                    </td>
                    <td className={tdClass}>
                      {formatPrice(sub.minforeignerPrice, "USD")}
                    </td>
                    <td className={tdClass}>
                      {formatPrice(sub.maxforeignerPrice, "USD")}
                    </td>
                    <td className={`${tdClass} max-w-[140px]`}>
                      <span className="line-clamp-2">{displayNote(sub.note)}</span>
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          className="p-1.5 text-slate-600 hover:text-primary hover:bg-primary/10 rounded-md transition-colors"
                          onClick={() => onEditSub(sub)}
                          aria-label="Edit sub-treatment"
                          title="Edit"
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                          onClick={() => setSubDeleteCandidate(sub.id)}
                          aria-label="Delete sub-treatment"
                          title="Delete"
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {subDeleteCandidate && (
        <ConfirmationModal
          title="Delete Sub-Treatment"
          message="Are you sure you want to delete this sub-treatment?"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setSubDeleteCandidate(null)}
        />
      )}
    </div>
  );
};
